import React from "react";
import { cn } from "@/lib/utils";
import type { TimelineDateMarker } from "@/domains/documents/types/timeline";

interface TimelineStemProps {
  /** Height of the stem — pixels, or any CSS length (the Days view stretches
   *  it to `100%` of a container whose height comes from the day groups). */
  height: number | string;
  /** Date markers to display along the stem */
  dateMarkers?: TimelineDateMarker[];
  /** Additional CSS classes */
  className?: string;
}

/**
 * TimelineStem - Molecule component
 *
 * The central vertical line of the timeline with optional date markers.
 * Documents and clusters branch off from this stem.
 *
 * @example
 * ```tsx
 * <TimelineStem height={800} />
 * <TimelineStem height={800} dateMarkers={markers} />
 * ```
 */
export const TimelineStem = React.memo(function TimelineStem({
  height,
  dateMarkers = [],
  className,
}: TimelineStemProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col items-center transition-all duration-300 ease-out",
        className
      )}
      style={{ height }}
    >
      {/* Main vertical line. Flat #cacbce rather than a fading gradient: with
          the timeline's grey panel gone, the tail of a gradient disappears
          against the white page canvas. */}
      <div
        className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-[#cacbce] transition-all duration-300"
        style={{ height: "100%" }}
        aria-hidden="true"
      />

      {/* Head of the arrow, at the top — the timeline runs newest-first from
          the top down, so the point marks the FUTURE end. */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2" aria-hidden="true">
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="text-[#cacbce]">
          <path d="M6 0L12 8H0L6 0Z" fill="currentColor" />
        </svg>
      </div>

      {/* Date markers */}
      {dateMarkers.map((marker) => (
        <TimelineDateLabel key={marker.date.toISOString()} marker={marker} />
      ))}

      {/* Round cap at the bottom — the oldest end, where the timeline stops. */}
      <div
        className="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-[#cacbce]"
        aria-hidden="true"
      />
    </div>
  );
});

interface TimelineDateLabelProps {
  marker: TimelineDateMarker;
}

/**
 * TimelineDateLabel - Internal component for date markers on the stem
 */
const TimelineDateLabel = React.memo(function TimelineDateLabel({
  marker,
}: TimelineDateLabelProps) {
  return (
    <div
      className="absolute left-1/2 flex -translate-x-1/2 items-center"
      style={{ top: marker.pixelPosition }}
    >
      {/* Tick mark */}
      <div
        className={cn(
          "absolute left-1/2 -translate-x-1/2",
          marker.isMinor ? "h-0.5 w-2 bg-[#cacbce]/60" : "h-0.5 w-3 bg-[#cacbce]"
        )}
        aria-hidden="true"
      />

      {/* Date label - centered on the stem */}
      {!marker.isMinor && (
        <span
          className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground"
          style={{ top: "-0.75rem" }}
        >
          {marker.label}
        </span>
      )}
    </div>
  );
});
